import {
  PopoverContentProps,
  PopoverProps,
  useDisclosure,
  usePrevious,
} from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InputAttributes } from "react-number-format";
import { TextInputProps } from "shared/components/input/text/TextInput";
import { useActiveElement } from "shared/hooks/useActiveElement";
import { useElementRect } from "shared/hooks/useElementRect";
import { isWritingSystemKeyEvent } from "shared/utils/KeyboardEventUtils";
import { useWindowSize } from "usehooks-ts";

export interface UseInputPopoverProps<T> {
  value: T | undefined;
  onChangeValue: (value: T | undefined) => void;
  onInputKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  getValueFromInput: (input: string) => T | undefined;
  getDisplayValue: (item: T) => string;
  setPendingValue?: (value: string) => void;
  isDisabled: boolean | undefined;
  updateWhileTyping?: boolean;
  isScrollable?: boolean;
}

export interface UseInputPopoverResult {
  inputRef: React.RefObject<HTMLInputElement | undefined>;
  inputProps: TextInputProps;
  popoverProps: PopoverProps;
  popoverContentProps: PopoverContentProps;
  pendingValue: string;
  setPendingValue: (value: string) => void;
  onClose: () => void;
  isOpen: boolean;
  isFocused: boolean;
}

export const useInputPopover = <T,>({
  value,
  onChangeValue,
  onInputKeyDown: onInputKeyDownProp,
  getDisplayValue,
  getValueFromInput,
  isDisabled,
  updateWhileTyping,
  isScrollable,
}: UseInputPopoverProps<T>): UseInputPopoverResult => {
  const windowSize = useWindowSize();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingValue, setPendingValue] = useState("");

  const activeElement = useActiveElement();
  const prevActiveElement = usePrevious(activeElement);

  const inputRef = useRef<HTMLInputElement>();
  const isFocused = activeElement === inputRef.current;
  const inputRect = useElementRect(inputRef);

  const displayValue = useMemo(
    () => (isOpen ? pendingValue : value ? getDisplayValue(value) : ""),
    [getDisplayValue, isOpen, pendingValue, value],
  );

  const onInputChangeValue = useCallback(
    (inputValue: string) => {
      setPendingValue(inputValue);
      if (updateWhileTyping) {
        const parsedValue = getValueFromInput(inputValue);
        onChangeValue(parsedValue);
      }
      if (isFocused && !isOpen) {
        onOpen();
      }
    },
    [
      getValueFromInput,
      isFocused,
      isOpen,
      onChangeValue,
      onOpen,
      updateWhileTyping,
    ],
  );

  const onInputFocus = useCallback(() => {
    setPendingValue(displayValue);
  }, [displayValue]);

  const onInputBlur = useCallback(() => {
    setPendingValue("");
    onClose();
  }, [onClose]);

  const onClickClear = useCallback(() => {
    setPendingValue("");
    onChangeValue(undefined);
  }, [onChangeValue]);

  const onInputKeyDown = useCallback<NonNullable<InputAttributes["onKeyDown"]>>(
    (event) => {
      onInputKeyDownProp?.(event);
      if (event.isPropagationStopped()) {
        return;
      }
      if (isOpen && event.key === "Escape") {
        event.stopPropagation();
        onClose();
      } else if (isOpen && event.key === "Enter") {
        const parsedValue = getValueFromInput(pendingValue);
        onChangeValue(parsedValue);
        event.stopPropagation();
        onClose();
      } else if (
        !isOpen &&
        (isWritingSystemKeyEvent(event) || event.key === "ArrowDown")
      ) {
        onInputFocus();
        onOpen();
      }
    },
    [
      getValueFromInput,
      isOpen,
      onChangeValue,
      onClose,
      onInputFocus,
      onInputKeyDownProp,
      onOpen,
      pendingValue,
    ],
  );

  const onInputMouseDown = useCallback(() => {
    if (!isOpen) {
      onInputFocus();
      onOpen();
    }
  }, [isOpen, onInputFocus, onOpen]);

  // this is MUCH more reliable than using onFocus/onBlur on the Input
  useEffect(() => {
    if (activeElement === prevActiveElement) {
      return;
    }
    if (activeElement === inputRef.current) {
      onInputFocus();
    } else {
      onInputBlur();
    }
  }, [activeElement, onInputFocus, onInputBlur, prevActiveElement]);

  const popoverProps: PopoverProps = {
    isOpen: isDisabled ? false : isOpen,
    // onClose,
    isLazy: true,
    lazyBehavior: "unmount",
    placement: "bottom-start",
    returnFocusOnClose: false,
    // do not focus the popup when it opens
    autoFocus: false,
    // closeOnEsc does not work because the PopoverContent is never focused
    // instead this is handled manually by onInputKeyDown above
    closeOnEsc: false,
    // this is handled by watching activeElement above
    closeOnBlur: false,
    // offset for border
    offset: [-1, 1],
  };

  const inputProps: TextInputProps = {
    onClear: onClickClear,
    onKeyDown: onInputKeyDown,
    onMouseDown: onInputMouseDown,
    onChangeValue: onInputChangeValue,
    value: displayValue,
    isEditing: isOpen,
    isDisabled,
  };

  const maxHeight = isScrollable
    ? Math.min(windowSize.height - inputRect.top - inputRect.height - 16, 400)
    : undefined;

  const popoverContentProps: PopoverContentProps = {
    width: inputRect.width + 2,
    maxHeight,
    onMouseDown: (event) => {
      // Prevent TextInput blur if clicking inside popup
      event.preventDefault();
    },
  };

  return {
    inputRef,
    inputProps,
    popoverProps,
    popoverContentProps,
    pendingValue,
    setPendingValue,
    onClose,
    isOpen,
    isFocused,
  };
};
